'use client'

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Globe, Check, ChevronDown } from 'lucide-react'
import { getFlagByCode } from '@/components/ui/flags'

const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'sv', name: 'Svenska' },
  { code: 'de', name: 'Deutsch' },
  { code: 'id', name: 'Bahasa Indonesia' },
  { code: 'ru', name: 'Русский' },
  { code: 'zh-CN', name: '简体中文' },
]

export default function LanguagePicker() {
  const { i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0]
  const CurrentFlag = getFlagByCode(currentLanguage.code)

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode)
    setIsOpen(false)
  }

  return (
    <div className="relative">
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2.5 px-3.5 py-2 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all duration-300 shadow-xl"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        style={{ color: '#fff' }}
      >
        <span className="inline-flex items-center shadow-sm"><CurrentFlag className="w-5 h-3.5 rounded-sm" /></span>
        <span className="text-sm font-semibold hidden lg:block opacity-90">
          {currentLanguage.name}
        </span>
        <ChevronDown size={14} className={`opacity-40 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60]"
              onClick={() => setIsOpen(false)}
            />

            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="absolute top-full right-0 mt-3 w-56 bg-[#12121f]/98 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/10 z-[70] overflow-hidden p-1.5"
            >
              <div className="grid grid-cols-1 gap-1">
                {languages.map((language) => {
                  const Flag = getFlagByCode(language.code)
                  const isActive = i18n.language === language.code
                  return (
                    <motion.button
                      key={language.code}
                      onClick={() => handleLanguageChange(language.code)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-xl transition-all duration-200 ${isActive ? 'bg-white/10 text-white' : 'hover:bg-white/5 text-white/70 hover:text-white'
                        }`}
                      whileHover={{ x: 4 }}
                    >
                      <span className="inline-flex items-center shadow-sm"><Flag className="w-5 h-3.5 rounded-sm" /></span>
                      <span className="flex-1 text-sm font-medium">
                        {language.name}
                      </span>
                      {isActive && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="text-[#d4a843]"
                        >
                          <Check className="w-4 h-4" />
                        </motion.div>
                      )}
                    </motion.button>
                  )
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
